import { createSelector } from 'reselect'
import config from 'constants/config'
import { isBookingProtectionAvailableForAccommodationOffer } from 'lib/checkout/bookingProtection/utils'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { isStandaloneExperience } from 'selectors/checkoutSelectors'
import { isInsuranceBookingProtectionItem, isExperienceItem, isBNBLLEHotelItem, isLuxPlusSubscriptionItem, isSubscriptionJoinItem, isInsuranceItem, isInstantBookingLEHotelItem, isBedbankItem, isTransferItem } from 'lib/checkout/checkoutUtils'
import isCheckoutEsimUpsellExperienceItem from 'loyalty/lib/esims/esimCheckoutUtils'
import { min, nonNullable } from 'lib/array/arrayUtils'
import verifyItemFollowsRegionalRules from 'lib/insurance/verifyItemFollowsRegionalRules'
import { isSpoofed } from 'selectors/isSpoofed'
import { STANDALONE_FLIGHT_CFMR_REGIONS } from 'constants/config/region'

export const isInsuranceBookingProtectionAvailableForCartItemTypes = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => isStandaloneExperience(state),
  (items, offers, isGift, isStandaloneExperience) => {
    if (isGift) {
      return false
    }

    const isInsurancePostPurchase = items.every(item => isInsuranceBookingProtectionItem(item) || isInsuranceItem(item))
    if (isInsurancePostPurchase) return true

    const filteredItems = items.filter(item =>
      !isLuxPlusSubscriptionItem(item) &&
      !isSubscriptionJoinItem(item) &&
      !isInsuranceBookingProtectionItem(item) &&
      !isInsuranceItem(item) &&
      !isTransferItem(item) &&
      !isCheckoutEsimUpsellExperienceItem(item),
    )

    if (!filteredItems.length) return false

    for (const item of filteredItems) {
      if (!isEnabledForFeature(config.UNIVERSAL_CHECKOUT_INSURANCE_BOOKING_PROTECTION_ENABLED_LIST, item.itemType)) {
        return false
      }

      // Experiences that are BNBL are not eligible
      if (isExperienceItem(item) && item.isBuyNowBookLater) {
        return false
      }

      // BNB LE Hotel items are not eligible
      if (isBNBLLEHotelItem(item)) {
        return false
      }

      // For any hotel item, ensure the offer supports booking protection
      if (isInstantBookingLEHotelItem(item) || isBedbankItem(item)) {
        const offer = offers[item.offerId]
        if (offer && !isBookingProtectionAvailableForAccommodationOffer(offer)) {
          return false
        }
      }
    }

    if (isStandaloneExperience) {
      const experienceItemType: App.Checkout.ExperienceItemType = 'experience'
      if (!isEnabledForFeature(config.UNIVERSAL_CHECKOUT_INSURANCE_BOOKING_PROTECTION_ENABLED_LIST, experienceItemType)) return false
    }

    return true
  },
)

const getOfferCountryCode = (item: App.Checkout.InstantBookingLEHotelItem | App.Checkout.BedbankHotelItem, offers: { [offerId: string]: App.Offer | App.BedbankOffer }): string | undefined => {
  const offer: any = offers[item.offerId]

  if (isInstantBookingLEHotelItem(item)) {
    return offer?.property?.geoData?.countryCode
  }

  if (isBedbankItem(item)) {
    return offer?.property?.address?.countryCode
  }
}
const isBookingProtectionAvailableForHotelRegion = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.bedbankOffers,
  (items, leOffers, bedbankOffers) => {
    // Filter hotel items (LE hotel or Bedbank)
    const hotelItems = items.filter(item => isBedbankItem(item) || isInstantBookingLEHotelItem(item))

    // Collect earliest check-in date from hotel items (Instant LE Hotel or Bedbank)
    const checkInDates = nonNullable(
      hotelItems.map(item => {
        if (isInstantBookingLEHotelItem(item) || isBedbankItem(item)) {
          return new Date((item).checkIn)
        }
      }),
    )
    const earliestCheckinDate = min(checkInDates)

    const offers = { ...leOffers, ...bedbankOffers }

    // Evaluate hotel items for regional rules and offer eligibility
    for (const item of hotelItems) {
      const countryCode = getOfferCountryCode(item, offers)
      const isValidRegionRules = verifyItemFollowsRegionalRules(countryCode, earliestCheckinDate)
      if (!isValidRegionRules) return false
    }

    return true
  },
)

/**
 * Whether the current region offers CFMR (insurance booking-protection) as the insurance
 * option on standalone flights checkout. Used to relax the standalone-flights guards so the
 * CFMR component is shown (regions like AU/NZ continue to use travel protection instead).
 */
export const isStandaloneFlightCfmrRegion = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (currentRegionCode): boolean => STANDALONE_FLIGHT_CFMR_REGIONS.includes(currentRegionCode),
)

export const isInsuranceBookingProtectionAvailableForRegions = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => isBookingProtectionAvailableForHotelRegion(state),
  (state: App.State) => isSpoofed(state),
  (currentRegionCode, isBookingProtectionAvailableForHotelOfferEligibility, isSpoofedUser) => {
    const isEnabled = isEnabledForFeature(config.INSURANCE_BOOKING_PROTECTION_ENABLED_REGIONS, currentRegionCode) && config.INSURANCE_BOOKING_PROTECTION_ENABLED
    if (!isEnabled) return false
    if (!isBookingProtectionAvailableForHotelOfferEligibility && !isSpoofedUser) return false
    return true
  },
)
